import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Button from '@/components/base/Button';
import { heroAssets } from '@/mocks/home';

gsap.registerPlugin(ScrollTrigger);

const trust = [
  { icon: 'ri-team-line', label: 'Professional Cleaning Team' },
  { icon: 'ri-hand-heart-line', label: 'Care in Every Detail' },
];

/* Scroll progress map (tunable) */
const VIDEO_START = 0.06;
const VIDEO_END = 0.88;
const SCROLL_DISTANCE = '+=320%';
const MEDIA_POS = 'object-[78%_center] md:object-[70%_center] lg:object-center';

const ENABLE_QUERY = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const posterRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const introRef = useRef<HTMLDivElement>(null);

  // Intro fade-up via Web Animations API (not a CSS class): ScrollTrigger.refresh() briefly
  // moves the pinned section in the DOM, which restarts CSS animations but not script animations.
  useLayoutEffect(() => {
    const el = introRef.current;
    if (!el || typeof el.animate !== 'function') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const anim = el.animate(
      [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 800, easing: 'ease', fill: 'backwards' },
    );
    return () => anim.cancel();
  }, []);
  // Read the media query on first render so the video/pin exist in the very first commit.
  const [enabled, setEnabled] = useState(() => typeof window !== 'undefined' && window.matchMedia(ENABLE_QUERY).matches);
  // Video download starts right after the poster has painted (not on scroll), so the two don't compete.
  const [videoArmed, setVideoArmed] = useState(false);

  useEffect(() => {
    if (!enabled || videoArmed) return undefined;
    const poster = posterRef.current;
    const arm = () => setVideoArmed(true);
    if (!poster || (poster.complete && poster.naturalWidth > 0)) {
      arm();
      return undefined;
    }
    poster.addEventListener('load', arm, { once: true });
    poster.addEventListener('error', arm, { once: true });
    const fallback = window.setTimeout(arm, 1500);
    return () => {
      poster.removeEventListener('load', arm);
      poster.removeEventListener('error', arm);
      window.clearTimeout(fallback);
    };
  }, [enabled, videoArmed]);

  useEffect(() => {
    const mq = window.matchMedia(ENABLE_QUERY);
    const update = () => setEnabled(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Layout effect: the ScrollTrigger pin wraps the section before first paint,
  // so the intro fade-up is not restarted by that DOM move (which caused the double play).
  useLayoutEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    const poster = posterRef.current;
    if (!enabled || !section || !video || !poster) return undefined;

    let duration = 0;
    let ready = false;
    let failed = false;
    let progress = 0;
    let targetTime = 0;
    let smoothTime = 0;
    let rafId = 0;
    let lastTs = 0;

    const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

    const applyPoster = () => {
      // Poster stays on top until a real video frame exists
      const o = ready && !failed ? 1 - clamp01(progress / VIDEO_START) : 1;
      poster.style.opacity = String(o);
    };

    const tick = (ts: number) => {
      rafId = 0;
      if (!ready || failed) return;
      // Frame-rate independent easing (~0.35s glide) so mouse-wheel steps become one smooth motion
      const dt = lastTs ? Math.min(0.05, (ts - lastTs) / 1000) : 1 / 60;
      lastTs = ts;
      const k = 1 - Math.exp(-dt / 0.12);
      const diff = targetTime - smoothTime;
      smoothTime = Math.abs(diff) < 0.004 ? targetTime : smoothTime + diff * k;
      if (!video.seeking && Math.abs(video.currentTime - smoothTime) > 0.02) {
        video.currentTime = smoothTime;
      }
      if (smoothTime !== targetTime || video.seeking) {
        rafId = requestAnimationFrame(tick);
      } else {
        lastTs = 0;
      }
    };
    const requestSeek = () => {
      if (!rafId) rafId = requestAnimationFrame(tick);
    };

    const computeTarget = () => {
      const local = clamp01((progress - VIDEO_START) / (VIDEO_END - VIDEO_START));
      targetTime = Math.max(0, Math.min(duration - 0.05, local * duration));
    };

    const onMeta = () => {
      duration = Number.isFinite(video.duration) ? video.duration : 0;
      computeTarget();
      ScrollTrigger.refresh();
    };
    const onData = () => {
      if (duration <= 0) onMeta();
      ready = duration > 0;
      applyPoster();
      requestSeek();
    };
    const onError = () => {
      failed = true;
      poster.style.opacity = '1';
    };

    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('loadeddata', onData);
    video.addEventListener('error', onError);
    if (video.readyState >= 1) onMeta();
    if (video.readyState >= 2) onData();

    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(section);
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: SCROLL_DISTANCE,
          pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            progress = self.progress;
            applyPoster();
            if (duration > 0) {
              computeTarget();
              requestSeek();
            }
          },
        },
      });
      tl.to(q('[data-hero-intro]'), { autoAlpha: 0, y: -24, duration: 0.1 }, 0)
        .to(q('[data-hero-cards]'), { autoAlpha: 0, y: 16, duration: 0.08 }, 0.02)
        .to(q('[data-hero-wash]'), { autoAlpha: 0, duration: 0.1 }, 0)
        .fromTo(q('[data-micro="1"]'), { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.05 }, 0.22)
        .to(q('[data-micro="1"]'), { autoAlpha: 0, y: -10, duration: 0.05 }, 0.38)
        .fromTo(q('[data-micro="2"]'), { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.05 }, 0.66)
        .to(q('[data-micro="2"]'), { autoAlpha: 0, y: -10, duration: 0.05 }, 0.8)
        .fromTo(q('[data-manifesto-veil]'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.06 }, 0.88)
        .fromTo(q('[data-manifesto]'), { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.08 }, 0.9)
        .to({}, { duration: 0.02 }, 0.98);
    }, section);

    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);
    if (!poster.complete) poster.addEventListener('load', onLoad, { once: true });
    const refreshTimer = window.setTimeout(onLoad, 300);

    return () => {
      window.clearTimeout(refreshTimer);
      window.removeEventListener('load', onLoad);
      poster.removeEventListener('load', onLoad);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('loadeddata', onData);
      video.removeEventListener('error', onError);
      if (rafId) cancelAnimationFrame(rafId);
      ctx.revert();
      poster.style.opacity = '';
    };
  }, [enabled]);

  return (
    <section ref={sectionRef} id="home" className="px-3 md:px-4 pt-[76px] md:pt-[80px] bg-background-50">
      <div className="relative w-full h-[640px] md:h-[720px] lg:h-[calc(100vh-96px)] lg:min-h-[620px] lg:max-h-[900px] rounded-[24px] overflow-hidden bg-background-200" data-hero-media>
        {/* Video sits beneath the poster; poster only fades once a decoded frame exists */}
        {enabled && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover ${MEDIA_POS} pointer-events-none`}
            src={videoArmed ? heroAssets.video : undefined}
            poster={heroAssets.poster}
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            aria-hidden="true"
            tabIndex={-1}
          />
        )}
        <img
          ref={posterRef}
          src={heroAssets.poster}
          alt="CLEARY professional home cleaner with spray bottle"
          title="CLEARY professional home cleaning"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover ${MEDIA_POS} pointer-events-none`}
        />
        <div data-hero-wash className="absolute inset-0 pointer-events-none bg-gradient-to-b from-background-50/80 via-background-50/30 to-transparent md:bg-gradient-to-r md:from-background-50/60 md:via-background-50/10 md:to-transparent" />

        {enabled && (
          <>
            <p data-micro="1" className="invisible absolute left-[64px] bottom-12 z-10 text-[13px] font-medium tracking-[0.16em] uppercase text-foreground-950 bg-background-50/75 rounded-full px-4 py-2 pointer-events-none">Care in Every Detail</p>
            <p data-micro="2" className="invisible absolute left-[64px] bottom-12 z-10 text-[13px] font-medium tracking-[0.16em] uppercase text-foreground-950 bg-background-50/75 rounded-full px-4 py-2 pointer-events-none">A Fresh Finish</p>
            <div data-manifesto-veil className="invisible absolute inset-0 bg-background-50/25 pointer-events-none" />
            <div data-manifesto className="invisible absolute inset-0 z-10 flex items-center justify-center px-6 pointer-events-none">
              <h2 className="font-heading text-center text-[64px] leading-[1.08] font-medium tracking-tight text-foreground-950">
                Professional Care,
                <br />
                Visible Results.
              </h2>
            </div>
          </>
        )}

        <div className="relative z-20 h-full w-full flex flex-col justify-between px-5 md:px-12 lg:px-[64px] pt-10 md:pt-16 lg:pt-[72px] pb-6 md:pb-10 pointer-events-none">
          <div data-hero-intro className="max-w-[620px] pointer-events-auto">
            <div ref={introRef}>
            <p className="text-xs md:text-[13px] font-medium tracking-[0.16em] text-foreground-950 mb-5">PROFESSIONAL HOME CLEANING</p>
            <h1 className="font-heading text-[40px] md:text-[60px] lg:text-[72px] leading-[1.05] font-medium tracking-tight text-foreground-950">Making Every Space Spotless.</h1>
            <p className="mt-6 text-base md:text-lg leading-[1.6] text-foreground-900 max-w-[440px]">Thoughtful home cleaning, delivered with care and attention to every detail.</p>
            <div className="mt-8 flex flex-wrap items-center gap-3 max-sm:translate-y-[245px]">
              <Button href="#quote">Get a Quote <i className="ri-arrow-right-line" /></Button>
              <Button href="#services" variant="ghost">Explore Services</Button>
            </div>
            </div>
          </div>
          <div data-hero-cards className="hidden sm:flex flex-wrap gap-3 pointer-events-auto">
            {trust.map((t) => (
              <div key={t.label} className="flex items-center gap-3 rounded-2xl bg-background-50/75 border border-background-50/60 backdrop-blur-sm px-4 py-3">
                <span className="w-9 h-9 rounded-full bg-background-200 text-secondary-500 flex items-center justify-center"><i className={`${t.icon} text-lg`} /></span>
                <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}